import DynamicForm, { DynamicFormRef, EDITOR_INPUT } from "@mavichpe/dynamic-forms";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { fetchSuscription, SuscriptionDataType, updateSuscription } from "@/api/SuscriptionsApi";
import { SuscriptionFormData } from "@/formTypes/ProfileFormDataType";
import { DocumentEmailTemplateIds } from "@pages/user/EmailTemplates";
import { getTenantId } from "@utils/Auth";
import { showAlert } from "@utils/Alert";
import { mirrorLogoToEmailAssets, uploadEmailImage } from "@utils/emailAssets";
import { buildDefaultEmailTemplate } from "@amplifyShared/tenantDefaults";

type EmailTemplateFormProps = {
  templateId: DocumentEmailTemplateIds;
  suscriptionId?: string;
  /** Tenant dueno de la plantilla. Solo se pasa en modo admin; si no, sale del token. */
  tenantId?: string;
} & React.HTMLAttributes<HTMLAnchorElement>;

export type EmailTemplateFormHandle = {
  /** Reemplaza el contenido del editor por la plantilla por defecto de este tipo, sin guardar. */
  resetToDefault: () => Promise<void>;
};

/** TinyMCE deja el `src` como blob:/data: mientras la subida no termina. */
const PENDING_IMAGE_SRC = /src\s*=\s*["'](?:blob:|data:image)/i;

type TinyMceBlobInfo = { blob: () => Blob; filename: () => string };
type TinyMceEditor = {
  ui: { registry: { addButton: (name: string, spec: Record<string, unknown>) => void } };
  insertContent: (html: string) => void;
};

const EmailTemplateForm = forwardRef<EmailTemplateFormHandle, EmailTemplateFormProps>((props, ref) => {
  const { templateId, suscriptionId, tenantId } = props
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [suscription, setSuscription] = useState<SuscriptionDataType>({});

  // TinyMCE lee su configuracion una sola vez al montar, y eso pasa antes de que carguen
  // la suscripcion y el tenant. Los handlers leen de refs para no quedarse con el valor
  // capturado en el primer render.
  const suscriptionRef = useRef<SuscriptionDataType>({});
  const tenantIdRef = useRef<string>(tenantId || "");
  const pendingUploadsRef = useRef<number>(0);
  const formRef = useRef<DynamicFormRef>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchSuscription(suscriptionId);
        setSuscription(data);
        suscriptionRef.current = data;
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    loadData();
  }, [suscriptionId]);

  useEffect(() => {
    if (tenantId) {
      tenantIdRef.current = tenantId;
      return;
    }
    getTenantId()
      .then((id) => { tenantIdRef.current = id; })
      .catch(() => { tenantIdRef.current = ""; });
  }, [tenantId]);

  const resolveTenantId = async (): Promise<string> => {
    if (!tenantIdRef.current) {
      tenantIdRef.current = await getTenantId();
    }
    return tenantIdRef.current;
  }

  /** Sube la imagen a S3 y devuelve la URL publica que TinyMCE pone en el `src`. */
  const handleImageUpload = async (blobInfo: TinyMceBlobInfo): Promise<string> => {
    pendingUploadsRef.current += 1;
    try {
      const owner = await resolveTenantId();
      return await uploadEmailImage(blobInfo.blob(), owner, blobInfo.filename());
    } catch (error) {
      const message = error instanceof Error ? error.message : "No fue posible subir la imagen";
      showAlert("danger", message);
      // `remove: true` hace que TinyMCE quite la imagen rota del contenido.
      return Promise.reject({ message, remove: true });
    } finally {
      pendingUploadsRef.current -= 1;
    }
  }

  const registerLogoButton = (editor: TinyMceEditor) => {
    editor.ui.registry.addButton("easyposLogo", {
      icon: "image",
      tooltip: "Insertar logo de la empresa",
      onAction: async () => {
        try {
          const owner = await resolveTenantId();
          const url = await mirrorLogoToEmailAssets(owner, `${suscriptionRef.current?.logo || ""}`);
          editor.insertContent(`<img src="${url}" alt="Logo" style="max-width:200px;" />`);
        } catch (error) {
          const message = error instanceof Error
            ? error.message
            : "No fue posible insertar el logo de la empresa";
          showAlert("danger", message);
        }
      },
    });
  }

  /** Reemplaza el contenido del editor por la plantilla por defecto, sin guardar. */
  const resetToDefault = async (): Promise<void> => {
    let logoUrl: string | undefined;
    try {
      const owner = await resolveTenantId();
      logoUrl = await mirrorLogoToEmailAssets(owner, `${suscriptionRef.current?.logo || ""}`);
    } catch {
      // La suscripcion puede no tener logo todavia: la plantilla se genera sin ese bloque.
      logoUrl = undefined;
    }

    const html = buildDefaultEmailTemplate(templateId, {
      nombre: suscriptionRef.current?.nombre,
      telefono1: suscriptionRef.current?.telefono1,
      email: suscriptionRef.current?.email,
      logoUrl,
    });
    formRef.current?.setFormValue(templateId, html, "String");
  }

  useImperativeHandle(ref, () => ({ resetToDefault }));

  const updateProfile = async (formData: Partial<SuscriptionFormData>) => {
    const templateHtml = `${formData[templateId] || ""}`;
    if (pendingUploadsRef.current > 0 || PENDING_IMAGE_SRC.test(templateHtml)) {
      showAlert("warning", "Espere a que terminen de subirse las imagenes antes de guardar");
      return;
    }

    setIsLoading(true);
    try {
      if (formData?.id) {
        await updateSuscription(formData.id, formData as SuscriptionDataType);
      }
    } finally {
      setIsLoading(false);
    }
  }

  const formConfig = () => {
    return {
      sections: [
        {
          hideBorderBottom: true,
          "fields": [
            [
              {
                "type": EDITOR_INPUT,
                "name": templateId,
                "label": false,
                "inputConfig": {
                  "editorInit": {
                    toolbar: 'undo redo | blocks | bold italic forecolor backcolor | \
            alignleft aligncenter alignright alignjustify | \
            bullist numlist outdent indent | removeformat | image easyposLogo link | code preview | help',
                    // Las imagenes deben quedar como URL publica: los `data:` URI los bloquean
                    // casi todos los clientes de correo.
                    automatic_uploads: true,
                    paste_data_images: true,
                    image_advtab: true,
                    images_upload_handler: handleImageUpload,
                    setup: registerLogoButton,
                  }
                }
              }
            ]
          ]
        }
      ]
    }
  }

  return (
    <>
      <DynamicForm<SuscriptionFormData>
        id="emailTemplate"
        ref={formRef}
        externalFormState={suscription}
        formConfig={formConfig()}
        isLoading={isLoading}
        onSubmit={updateProfile} >
      </DynamicForm>
    </>
  )
});

export default EmailTemplateForm;
